import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import {
  Stack,
  Grid,
  Box,
  useMantineTheme,
} from "@mantine/core";
import DataChart from "../Shared/charts";
import { useMediaQuery } from "@mantine/hooks";
import { CustomSelect, SelectOption } from "../Shared/customSelect";
import { useAppSelector } from "../../store/hooks";
import {
  fetchDashboardEndFilterQuery,
  fetchDashboardStartFilterQuery,
  fetchFacilitiesListQuery,
  fetchSpecialtiesListQuery,
  useGraphData,
} from "../../hooks/queries";

// Define chart types that match your DataChart component
type ChartType = "bar" | "horizontalBar" | "line" | "pie";

// Define TypeScript interfaces based on actual API response
interface ApiChart {
  chart_title: string;
  chart_type:
    | "Vertical Graph"
    | "Horizontal Graph"
    | "Line Graph"
    | "Pie Chart";
  x_axis?: {
    title: string | null;
    labels: string[];
  };
  y_axis?: {
    title: string | null;
    labels: string[];
  };
  series?: Array<{
    name: string;
    color: string;
    data: number[];
  }>;
  // Pie chart specific properties
  legends?: string[];
  data?: number[];
  colors?: string[];
  percentages?: string[];
}

// Extended interface for chart datasets to include custom properties
interface ExtendedChartDataset {
  label?: string;
  data: number[];
  backgroundColor: string | string[];
  borderColor?: string | string[];
  borderWidth?: number;
  tension?: number;
  // Custom properties for pie charts
  [key: string]: any;
}

// Extended ChartData interface
interface ExtendedChartData {
  labels: string[];
  datasets: ExtendedChartDataset[];
}

interface ApiData {
  graphs: ApiChart[];
}

interface FilterData {
  data: Array<{
    DISPLAY: string;
    VALUE: string | null;
  }>;
  total_count?: number;
  page?: number;
  limit?: number;
}

// Define ChartData type to match your DataChart component expectations
interface ChartData {
  labels: string[];
  datasets: Array<{
    label?: string;
    data: number[];
    backgroundColor: string | string[];
    borderColor?: string | string[];
    borderWidth?: number;
    tension?: number;
  }>;
}

// Query parameters interfaces
interface GraphDataParams {
  provider_npi?: string;
  facility?: string;
  specialty?: string;
  period_type?: string;
  week_start?: string;
  week_end?: string;
  month_start?: string;
  month_end?: string;
}

interface ListParams {
  page: string;
  limit: string;
  search?: string;
}

interface FilterParams {
  period_type: string;
}

const TestCharts = () => {
  const theme = useMantineTheme();
  const providerNPI = useAppSelector((state) => state?.provider?.providerNPI);
  const [loading, setLoading] = useState<boolean>(false);
  const [filtersLoading, setFiltersLoading] = useState<boolean>(true);
  const [dateFiltersLoading, setDateFiltersLoading] = useState<boolean>(false); // New state for date filters
  const [apiData, setApiData] = useState<ApiData | null>(null);
  const [facilitiesData, setFacilitiesData] = useState<FilterData | null>(null);
  const [specialtiesData, setSpecialtiesData] = useState<FilterData | null>(
    null
  );
  const [startFilterData, setStartFilterData] = useState<FilterData | null>(
    null
  );
  const [endFilterData, setEndFilterData] = useState<FilterData | null>(null);

  // Add state to track initialization
  const [isInitialized, setIsInitialized] = useState<boolean>(false);

  // Add search loading states for API search
  const [facilitiesSearchLoading, setFacilitiesSearchLoading] =
    useState<boolean>(false);
  const [specialtiesSearchLoading, setSpecialtiesSearchLoading] =
    useState<boolean>(false);

  // Add error states for each filter
  const [facilitiesError, setFacilitiesError] = useState<boolean>(false);
  const [specialtiesError, setSpecialtiesError] = useState<boolean>(false);
  const [startFilterError, setStartFilterError] = useState<boolean>(false);
  const [endFilterError, setEndFilterError] = useState<boolean>(false);
  const [periodTypeError] = useState<boolean>(false);

  // Add search term states for debouncing
  const [, setFacilitiesSearchTerm] = useState<string>("");
  const [, setSpecialtiesSearchTerm] =
    useState<string>("");

  // Refs for debounce timeouts
  const facilitiesSearchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const specialtiesSearchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Filter selections state - Changed to single select for facilities and specialties
  const [selectedFacilities, setSelectedFacilities] = useState<string[]>([]); // Empty initially
  const [selectedSpecialities, setSelectedSpecialities] = useState<string[]>(
    []
  ); // Empty initially
  const [selectedPeriodType, setSelectedPeriodType] = useState<string[]>(["M"]); // Default to Monthly as array
  const [selectedMonthStart, setSelectedMonthStart] = useState<string[]>([]);
  const [selectedMonthEnd, setSelectedMonthEnd] = useState<string[]>([]);

  // Query parameters state
  const [graphParams, setGraphParams] = useState<GraphDataParams>({
    provider_npi: providerNPI,
    facility: "",
    specialty: "",
    period_type: "",
    week_start: "",
    week_end: "",
    month_start: "",
    month_end: "",
  });

  const [facilitiesParams, setFacilitiesParams] = useState<ListParams>({
    page: "0",
    limit: "100",
    search: "",
  });

  const [specialtiesParams, setSpecialtiesParams] = useState<ListParams>({
    page: "0",
    limit: "100",
    search: "",
  });

  const [startFilterParams, setStartFilterParams] = useState<FilterParams>({
    period_type: "M",
  });

  const [endFilterParams, setEndFilterParams] = useState<FilterParams>({
    period_type: "M",
  });

  // Use Mantine's useMediaQuery hook to detect screen sizes
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`);
  const isTablet = useMediaQuery(`(max-width: ${theme.breakpoints.md})`);

  const getChartTitle = (chartTitle: string): string => {
    if (!apiData || !apiData.graphs) {
      return chartTitle;
    }

    const chart = apiData?.graphs?.find(
      (graph: ApiChart) => graph?.chart_title === chartTitle
    );
    return chart ? chart?.chart_title : chartTitle;
  };

  const getChartAxisTitles = (
    chartTitle: string
  ): { xAxisTitle?: string; yAxisTitle?: string } => {
    if (!apiData?.graphs) {
      return {};
    }

    const chart = apiData.graphs.find(
      (graph: ApiChart) => graph.chart_title === chartTitle
    );

    if (!chart) {
      return {};
    }

    return {
      xAxisTitle: chart.x_axis?.title ?? undefined,
      yAxisTitle: chart.y_axis?.title ?? undefined,
    };
  };
  // Calculate responsive dimensions based on screen size
  const getChartDimensions = () => {
    if (isMobile) {
      return { height: 300, width: "100%" };
    } else if (isTablet) {
      return { height: 350, width: "100%" };
    } else {
      return { height: 434, width: "100%" };
    }
  };
  // Update graphParams when providerNPI changes
  useEffect(() => {
    if (providerNPI) {
      setGraphParams((prev) => ({
        ...prev,
        provider_npi: providerNPI,
      }));
    }
  }, [providerNPI]);

  const { height, width } = getChartDimensions();

  // Create fallback/empty chart data
  const createEmptyChartData = (): ChartData => ({
    labels: [],
    datasets: [
      {
        label: "No Data",
        data: [],
        backgroundColor: "#f0f0f0",
        borderColor: "#d0d0d0",
      },
    ],
  });

  // Function to convert API filter data to SelectOption format.
  // Accepts either the raw array or the `{ data: [...] }` wrapper - some
  // list endpoints on this API return one shape, some the other.
  const convertToSelectOptions = (
    filterData: FilterData | FilterData["data"] | null
  ): SelectOption[] => {
    const list = Array.isArray(filterData) ? filterData : filterData?.data;

    if (!Array.isArray(list)) {
      return [];
    }

    return list
      .filter((item) => item?.DISPLAY !== null && item?.VALUE !== null) // Filter out null entries
      .map((item) => ({
        value: item?.VALUE as string,
        label: item?.DISPLAY,
      }));
  };

  // Create period type options for CustomSelect
  const periodTypeSelectOptions: SelectOption[] = [
    { value: "M", label: "Monthly" },
    { value: "W", label: "Weekly" },
  ];

  // Debounced search functions for facilities
  const handleFacilitiesSearch = useCallback(
    (searchTerm: string) => {
      setFacilitiesSearchTerm(searchTerm);

      // Clear previous timeout
      if (facilitiesSearchTimeoutRef.current) {
        clearTimeout(facilitiesSearchTimeoutRef.current);
      }

      // Set new timeout for debouncing
      facilitiesSearchTimeoutRef.current = setTimeout(async () => {
        setFacilitiesSearchLoading(true);
        setFacilitiesError(false); // Reset error state before new request
        console.log("🔍 Searching facilities with term:", searchTerm);

        try {
          const newFacilitiesParams = {
            ...facilitiesParams,
            search: searchTerm,
            page: "0", // Reset to first page when searching
          };

          const facilitiesResponse = await fetchFacilitiesListQuery(
            cleanParams(newFacilitiesParams)
          );
          console.log("✅ Facilities Search Response:", facilitiesResponse);

          setFacilitiesData(facilitiesResponse.data);
          setFacilitiesParams(newFacilitiesParams);
          setFacilitiesError(false); // Clear error on success
        } catch (error) {
          console.error("❌ Error searching facilities:", error);
          setFacilitiesError(true); // Set error state
          setFacilitiesData(null); // Clear data on error
        } finally {
          setFacilitiesSearchLoading(false);
        }
      }, 500); // 500ms debounce delay
    },
    [facilitiesParams]
  );

  // Debounced search functions for specialties
  const handleSpecialtiesSearch = useCallback(
    (searchTerm: string) => {
      setSpecialtiesSearchTerm(searchTerm);

      // Clear previous timeout
      if (specialtiesSearchTimeoutRef.current) {
        clearTimeout(specialtiesSearchTimeoutRef.current);
      }

      // Set new timeout for debouncing
      specialtiesSearchTimeoutRef.current = setTimeout(async () => {
        setSpecialtiesSearchLoading(true);
        setSpecialtiesError(false); // Reset error state before new request
        console.log("🔍 Searching specialties with term:", searchTerm);

        try {
          const newSpecialtiesParams = {
            ...specialtiesParams,
            search: searchTerm,
            page: "0", // Reset to first page when searching
          };

          const specialtiesResponse = await fetchSpecialtiesListQuery(
            cleanParams(newSpecialtiesParams)
          );
          console.log("✅ Specialties Search Response:", specialtiesResponse);

          setSpecialtiesData(specialtiesResponse?.data);
          setSpecialtiesParams(newSpecialtiesParams);
          setSpecialtiesError(false); // Clear error on success
        } catch (error) {
          console.error("❌ Error searching specialties:", error);
          setSpecialtiesError(true); // Set error state
          setSpecialtiesData(null); // Clear data on error
        } finally {
          setSpecialtiesSearchLoading(false);
        }
      }, 500); // 500ms debounce delay
    },
    [specialtiesParams]
  );

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (facilitiesSearchTimeoutRef.current) {
        clearTimeout(facilitiesSearchTimeoutRef.current);
      }
      if (specialtiesSearchTimeoutRef.current) {
        clearTimeout(specialtiesSearchTimeoutRef.current);
      }
    };
  }, []);

  // Get options for each filter
  const facilitiesOptions = convertToSelectOptions(facilitiesData);
  const specialitiesOptions = convertToSelectOptions(specialtiesData);
  const monthStartOptions = convertToSelectOptions(startFilterData);
  const monthEndOptions = convertToSelectOptions(endFilterData);

  // Helper function to extract week number from display string
  const getWeekNumber = (displayValue: string): number => {
    const weekMatch = displayValue?.match(/Week (\d+)/);
    return weekMatch ? parseInt(weekMatch[1], 10) : 0;
  };

  const getChartData = (chartTitle: string): ExtendedChartData => {
    if (!apiData || !apiData.graphs) {
      console.log(`⚠️ No API data available for chart: ${chartTitle}`);
      return createEmptyChartData();
    }

    console.log(`🔍 Looking for chart: "${chartTitle}"`);
    console.log(
      "📋 Available charts:",
      apiData.graphs.map((g) => `"${g.chart_title}"`)
    );

    const chart = apiData.graphs.find(
      (graph: ApiChart) => graph.chart_title === chartTitle
    );
    if (!chart) {
      console.log(`⚠️ Chart not found in API data: "${chartTitle}"`);
      return createEmptyChartData();
    }

    console.log(`✅ Found chart: "${chartTitle}"`);
    const convertedData = convertApiDataToChartFormat(chart);

    if (!convertedData) {
      console.log(`❌ Failed to convert data for chart: "${chartTitle}"`);
      return createEmptyChartData();
    }

    console.log(`✅ Successfully converted data for: "${chartTitle}"`);
    return convertedData;
  };

  // Function to convert API chart data to Chart.js format
  const convertApiDataToChartFormat = (
    apiChart: ApiChart
  ): ExtendedChartData | null => {
    if (!apiChart) {
      console.log("❌ No chart data provided");
      return null;
    }

    console.log(
      "🔍 Converting chart:",
      apiChart.chart_title,
      "Type:",
      apiChart.chart_type
    );
    console.log("📊 Chart data:", apiChart);

    // Handle different chart types
    switch (apiChart.chart_type) {
      case "Vertical Graph":
      case "Horizontal Graph":
        // Check if required axis data exists
        if (!apiChart.x_axis || !apiChart.y_axis) {
          console.error(
            "❌ Missing axis data for bar chart:",
            apiChart.chart_title
          );
          return null;
        }

        const barData: ExtendedChartData = {
          labels:
            apiChart?.chart_type === "Horizontal Graph"
              ? apiChart?.y_axis.labels
              : apiChart?.x_axis.labels,
          datasets:
            apiChart.series?.map((series: any) => ({
              label: series?.name,
              data: series?.data,
              backgroundColor: series?.color,
              borderColor: series?.color,
            })) || [],
        };
        console.log("📊 Bar chart converted data:", barData);
        return barData;

      case "Line Graph":
        // Check if x_axis exists for line charts
        if (!apiChart.x_axis) {
          console.error(
            "❌ Missing x_axis data for line chart:",
            apiChart.chart_title
          );
          return null;
        }

        const lineData: ExtendedChartData = {
          labels: apiChart?.x_axis.labels,
          datasets:
            apiChart.series?.map((series: any) => ({
              label: series?.name,
              data: series?.data,
              borderColor: series?.color,
              backgroundColor: "transparent",
              tension: 0.4,
            })) || [],
        };
        console.log("📈 Line chart converted data:", lineData);
        return lineData;

      case "Pie Chart":
        // Enhanced pie chart conversion with percentages
        const pieData: ExtendedChartData = {
          labels: apiChart?.legends || [],
          datasets: [
            {
              // Use percentages if available, otherwise fall back to raw data
              data: apiChart?.percentages
                ? apiChart?.percentages?.map((percent) =>
                    parseFloat(percent?.replace("%", ""))
                  )
                : apiChart?.data || [],
              backgroundColor: apiChart?.colors || [],
              borderWidth: 0,
              // Add custom property to track if we're using percentages
              _isPercentage: !!apiChart?.percentages,
              // Store original data for tooltips if needed
              _originalData: apiChart?.data || [],
              _originalPercentages: apiChart?.percentages || [],
            },
          ],
        };

        return pieData;

      default:
        console.warn("⚠️ Unknown chart type:", apiChart.chart_type);
        return null;
    }
  };
  // Function to get chart type for DataChart component
  const getChartType = (apiChartType: string): ChartType => {
    switch (apiChartType) {
      case "Vertical Graph":
        return "bar";
      case "Horizontal Graph":
        return "horizontalBar";
      case "Line Graph":
        return "line";
      case "Pie Chart":
        return "pie";
      default:
        return "bar";
    }
  };

  // Helper function to clean up parameters (remove empty values)
  const cleanParams = (params: any): any => {
    return Object?.fromEntries(
      Object?.entries(params)?.filter(
        ([_, value]) => value !== "" && value !== null && value !== undefined
      )
    );
  };

  // NEW: Function to reload date filters when period type changes
  const reloadDateFilters = async (periodType: string): Promise<void> => {
    setDateFiltersLoading(true);
    console.log("🔄 Reloading date filters for period type:", periodType);

    // Reset date filter error states
    setStartFilterError(false);
    setEndFilterError(false);

    // Clear existing date selections since they're no longer valid
    setSelectedMonthStart([]);
    setSelectedMonthEnd([]);

    // Update filter parameters with new period type
    const newStartFilterParams = { period_type: periodType };
    const newEndFilterParams = { period_type: periodType };

    try {
      const [startFilterResponse, endFilterResponse] = await Promise.allSettled(
        [
          fetchDashboardStartFilterQuery(newStartFilterParams),
          fetchDashboardEndFilterQuery(newEndFilterParams),
        ]
      );

      // Handle start filter response
      if (startFilterResponse?.status === "fulfilled") {
        console.log(
          "✅ Reloaded Start Date Filter Response:",
          startFilterResponse.value
        );
        setStartFilterData(startFilterResponse?.value?.data);
        setStartFilterError(false);
        setStartFilterParams(newStartFilterParams);
      } else {
        console.error(
          "❌ Error reloading start filter:",
          startFilterResponse.reason
        );
        setStartFilterError(true);
        setStartFilterData(null);
      }

      // Handle end filter response
      if (endFilterResponse?.status === "fulfilled") {
        console.log(
          "✅ Reloaded End Date Filter Response:",
          endFilterResponse.value
        );
        setEndFilterData(endFilterResponse?.value?.data);
        setEndFilterError(false);
        setEndFilterParams(newEndFilterParams);
      } else {
        console.error(
          "❌ Error reloading end filter:",
          endFilterResponse?.reason
        );
        setEndFilterError(true);
        setEndFilterData(null);
      }

      console.log(
        "🎉 Date filters reloaded successfully for period type:",
        periodType
      );
    } catch (error) {
      console.error("❌ Unexpected error during date filter reload:", error);
      setStartFilterError(true);
      setEndFilterError(true);
      setStartFilterData(null);
      setEndFilterData(null);
    } finally {
      setDateFiltersLoading(false);
    }
  };

  // Enhanced function to find current period option

  // Enhanced function to find current period option - FIXED FOR YOUR DATA FORMAT
  const findCurrentPeriodOption = (
    options: SelectOption[],
    periodType: string
  ): SelectOption | null => {
    const currentDate = new Date();

    if (periodType === "M") {
      const currentMonth = currentDate.getMonth() + 1; // getMonth() returns 0-11, so add 1
      const currentYear = currentDate.getFullYear();

      console.log(
        `🔍 Looking for current month: ${currentMonth} (${currentMonth
          .toString()
          .padStart(2, "0")}) in year ${currentYear}`
      );
      console.log(
        "📋 Available month options:",
        options?.map((opt) => ({ label: opt.label, value: opt.value }))
      );

      // 🚨 SPECIFIC LOGIC FOR YOUR DATA FORMAT
      // Current date: August 19, 2025 -> Should find "Aug (08-31-2025)"

      const currentMonthStr = currentMonth.toString().padStart(2, "0"); // "08" for August

      // Month abbreviations mapping
      const monthAbbreviations = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];

      const currentMonthAbbr = monthAbbreviations[currentMonth - 1]; // "Aug" for August

      console.log(
        `🔍 Looking for month abbreviation: "${currentMonthAbbr}" or month number: "${currentMonthStr}"`
      );

      // Strategy 1: Find exact match for current month
      for (const option of options) {
        const display = option.label; // e.g., "Aug (08-31-2025)"
        const value = option.value; // e.g., "08-01-2025 - 08-31-2025"

        console.log(`🔍 Checking option: "${display}" with value: "${value}"`);

        // Check if DISPLAY contains current month abbreviation AND current year
        const hasMonthAbbr = display.includes(currentMonthAbbr);
        const hasCurrentYear = display.includes(currentYear.toString());

        // Check if VALUE contains current month number AND current year
        const valueHasMonthNum =
          value.includes(`${currentMonthStr}-`) ||
          value.includes(`-${currentMonthStr}-`);
        const valueHasCurrentYear = value.includes(currentYear.toString());

        // Also check the specific pattern in your VALUE format: "MM-DD-YYYY"
        const valueStartsWithCurrentMonth = value.startsWith(
          `${currentMonthStr}-01-${currentYear}`
        );

        if (
          (hasMonthAbbr && hasCurrentYear) ||
          (valueHasMonthNum && valueHasCurrentYear) ||
          valueStartsWithCurrentMonth
        ) {
          console.log(`✅ Found current month option: "${display}"`);
          return option;
        }
      }

      console.log(`❌ Current month "${currentMonthAbbr}" not found`);

      // Strategy 2: Find the latest available month (closest to current date but not in future)
      console.log(
        "🔍 Looking for latest available month that's not in the future..."
      );

      let latestValidOption: SelectOption | null = null;
      let latestValidDate: Date | null = null;

      for (const option of options) {
        try {
          const value = option.value; // e.g., "08-01-2025 - 08-31-2025"

          // Extract start date from VALUE format "MM-DD-YYYY - MM-DD-YYYY"
          const startDateStr = value.split(" - ")[0]; // "08-01-2025"
          const [month, , year] = startDateStr
            .split("-")
            .map((num) => parseInt(num, 10));

          // Create date object for this month
          const optionDate = new Date(year, month - 1, 1); // month - 1 because Date months are 0-indexed

          console.log(
            `📅 Option "${option.label}" represents month: ${month}/${year}`
          );

          // Check if this month is not in the future (current month or earlier)
          const currentMonthStart = new Date(currentYear, currentMonth - 1, 1);

          if (optionDate <= currentMonthStart) {
            if (!latestValidDate || optionDate > latestValidDate) {
              latestValidDate = optionDate;
              latestValidOption = option;
              console.log(`📅 New latest valid option: "${option.label}"`);
            }
          } else {
            console.log(
              `📅 Option "${option.label}" is in the future, skipping`
            );
          }
        } catch (error) {
          console.error(`❌ Error parsing date from option:`, option, error);
        }
      }

      if (latestValidOption) {
        console.log(
          `✅ Found latest valid month: "${latestValidOption.label}"`
        );
        return latestValidOption;
      }

      // Strategy 3: If no valid past/current month found, use the last option in array
      console.log(
        "🔍 No valid past/current month found, using last option in array..."
      );

      if (options.length > 0) {
        const lastOption = options[options.length - 1];
        console.log(`📅 Using last option: "${lastOption.label}"`);
        return lastOption;
      }

      console.log("❌ No month options available at all");
      return null;
    } else if (periodType === "W") {
      // Weekly logic for your week data format
      const currentMonth = currentDate.getMonth() + 1;
      const currentDay = currentDate.getDate();
      const currentYear = currentDate.getFullYear();

      console.log(
        `🔍 Looking for current week containing: ${currentMonth}/${currentDay}/${currentYear}`
      );

      // For weekly data, check if current date falls within the week range
      for (const option of options) {
        try {
          const value = option.value; // e.g., "07-07-2025 - 07-13-2025"
          const [startDateStr, endDateStr] = value.split(" - ");

          // Parse dates (MM-DD-YYYY format)
          const parseDate = (dateStr: string): Date => {
            const [month, day, year] = dateStr
              .split("-")
              .map((num) => parseInt(num, 10));
            return new Date(year, month - 1, day);
          };

          const startDate = parseDate(startDateStr);
          const endDate = parseDate(endDateStr);
          const currentDateObj = new Date(
            currentYear,
            currentMonth - 1,
            currentDay
          );

          if (currentDateObj >= startDate && currentDateObj <= endDate) {
            console.log(`✅ Found current week: "${option.label}"`);
            return option;
          }
        } catch (error) {
          console.error(`❌ Error parsing week dates:`, option, error);
        }
      }

      // If current week not found, find next available week
      console.log(
        "📅 Current week not found, looking for next available week..."
      );
      // ... (similar logic for weeks)

      // Fallback to last option
      if (options.length > 0) {
        return options[options.length - 1];
      }

      return null;
    }

    return null;
  };

  // NEW: Function to set date selections after filters are reloaded
  const setDateSelectionsAfterReload = (
    startFilterResponse: FilterData | null,
    endFilterResponse: FilterData | null
  ) => {
    // Convert filter data to options (handle null data gracefully)
    const startOpts = convertToSelectOptions(startFilterResponse);
    const endOpts = convertToSelectOptions(endFilterResponse);

    // Get current period type
    const currentPeriodType =
      selectedPeriodType.length > 0 ? selectedPeriodType[0] : "M";

    console.log(
      "📅 Setting date selections after reload for period type:",
      currentPeriodType
    );

    if (currentPeriodType === "W") {
      // For weekly: Set start as first week, end as current week or next available week
      const firstStartOption =
        startOpts.length > 0 && !startFilterError ? [startOpts[0].value] : [];

      let endOption: string[] = [];
      if (endOpts.length > 0 && !endFilterError) {
        const currentWeekOption = findCurrentPeriodOption(
          endOpts,
          currentPeriodType
        );
        if (currentWeekOption) {
          endOption = [currentWeekOption.value];
          console.log(
            `📅 Found current/next week option:`,
            currentWeekOption.label
          );
        } else {
          // Fallback to last option if current week not found
          endOption = [endOpts[endOpts.length - 1].value];
          console.log(
            `📅 Current week not found, using last week option:`,
            endOpts[endOpts.length - 1].label
          );
        }
      }

      console.log("📅 Setting weekly selections:");
      console.log(`📅 Start week:`, firstStartOption);
      console.log(`📅 End week:`, endOption);

      setSelectedMonthStart(firstStartOption);
      setSelectedMonthEnd(endOption);
    } else {
      // For monthly: Set start as first month, end as current month
      const firstOption =
        startOpts.length > 0 && !startFilterError ? [startOpts[0].value] : [];

      let endOption: string[] = [];
      if (endOpts.length > 0 && !endFilterError) {
        const currentMonthOption = findCurrentPeriodOption(
          endOpts,
          currentPeriodType
        );
        if (currentMonthOption) {
          endOption = [currentMonthOption.value];
          console.log(
            `📅 Found current month option:`,
            currentMonthOption.label
          );
        } else {
          // Fallback to last option if current month not found
          endOption = [endOpts[endOpts.length - 1].value];
          console.log(
            `📅 Current month not found, using last option:`,
            endOpts[endOpts.length - 1].label
          );
        }
      }

      console.log("📅 Setting monthly selections:");
      console.log(`📅 Start month:`, firstOption);
      console.log(`📅 End month:`, endOption);

      setSelectedMonthStart(firstOption);
      setSelectedMonthEnd(endOption);
    }
  };

  // STEP 1: Load all filter APIs first
  const loadFilterAPIs = async (): Promise<void> => {
    setFiltersLoading(true);
    console.log("🚀 Step 1: Loading all filter APIs...");

    // Reset all error states before making API calls
    setFacilitiesError(false);
    setSpecialtiesError(false);
    setStartFilterError(false);
    setEndFilterError(false);

    try {
      const [
        facilitiesResponse,
        specialtiesResponse,
        startFilterResponse,
        endFilterResponse,
      ] = await Promise.allSettled([
        fetchFacilitiesListQuery(cleanParams(facilitiesParams)),
        fetchSpecialtiesListQuery(cleanParams(specialtiesParams)),
        fetchDashboardStartFilterQuery(startFilterParams),
        fetchDashboardEndFilterQuery(endFilterParams),
      ]);

      // Handle facilities response
      if (facilitiesResponse.status === "fulfilled") {
        console.log("✅ Facilities List Response:", facilitiesResponse.value);
        setFacilitiesData(facilitiesResponse.value.data);
        setFacilitiesError(false);
      } else {
        console.error(
          "❌ Error loading facilities:",
          facilitiesResponse.reason
        );
        setFacilitiesError(true);
        setFacilitiesData(null);
      }

      // Handle specialties response
      if (specialtiesResponse.status === "fulfilled") {
        console.log("✅ Specialties List Response:", specialtiesResponse.value);
        setSpecialtiesData(specialtiesResponse.value.data);
        setSpecialtiesError(false);
      } else {
        console.error(
          "❌ Error loading specialties:",
          specialtiesResponse.reason
        );
        setSpecialtiesError(true);
        setSpecialtiesData(null);
      }

      // Handle start filter response
      if (startFilterResponse.status === "fulfilled") {
        console.log(
          "✅ Start Date Filter Response:",
          startFilterResponse.value
        );
        setStartFilterData(startFilterResponse.value.data);
        setStartFilterError(false);
      } else {
        console.error(
          "❌ Error loading start filter:",
          startFilterResponse.reason
        );
        setStartFilterError(true);
        setStartFilterData(null);
      }

      // Handle end filter response
      if (endFilterResponse.status === "fulfilled") {
        console.log("✅ End Date Filter Response:", endFilterResponse.value);
        setEndFilterData(endFilterResponse.value.data);
        setEndFilterError(false);
      } else {
        console.error("❌ Error loading end filter:", endFilterResponse.reason);
        setEndFilterError(true);
        setEndFilterData(null);
      }

      console.log("🎉 Step 1 completed: Filter API loading finished!");
    } catch (error) {
      console.error("❌ Unexpected error during filter API calls:", error);
      // Set all filters to error state if unexpected error occurs
      setFacilitiesError(true);
      setSpecialtiesError(true);
      setStartFilterError(true);
      setEndFilterError(true);
    } finally {
      setFiltersLoading(false);
    }
  };

  // STEP 2: Set initial values based on loaded filter data
  const setInitialValues = (
    facilitiesResponse: FilterData | null,
    specialtiesResponse: FilterData | null,
    startFilterResponse: FilterData | null,
    endFilterResponse: FilterData | null
  ) => {
    console.log("🚀 Step 2: Setting initial values...");
    setDateSelectionsAfterReload(startFilterResponse, endFilterResponse);
    setIsInitialized(true);
  };

  // STEP 3: Update graph parameters and fetch graph data
  const updateGraphParamsAndFetchData = () => {
    console.log("🚀 Step 3: Updating graph params and fetching data...");

    const updates: Partial<GraphDataParams> = {};

    // Update facility parameter - Changed to single select
    if (selectedFacilities.length === 1) {
      updates.facility = selectedFacilities[0];
    } else {
      updates.facility = ""; // No facility selected
    }

    // Update speciality parameter - Changed to single select
    if (selectedSpecialities.length === 1) {
      updates.specialty = selectedSpecialities[0];
    } else {
      updates.specialty = ""; // No speciality selected
    }

    // Update period type parameter
    if (selectedPeriodType.length === 1) {
      updates.period_type = selectedPeriodType[0];
    }

    // Update date parameters based on period type
    if (selectedPeriodType[0] === "W") {
      // For weekly, use week_start and week_end
      updates.month_start = ""; // Clear month parameters
      updates.month_end = "";
      if (selectedMonthStart.length === 1) {
        updates.week_start = selectedMonthStart[0];
      } else {
        updates.week_start = "";
      }
      if (selectedMonthEnd.length === 1) {
        updates.week_end = selectedMonthEnd[0];
      } else {
        updates.week_end = "";
      }
    } else {
      // For monthly, use month_start and month_end
      updates.week_start = ""; // Clear week parameters
      updates.week_end = "";
      if (selectedMonthStart.length === 1) {
        updates.month_start = selectedMonthStart[0];
      } else {
        updates.month_start = "";
      }
      if (selectedMonthEnd.length === 1) {
        updates.month_end = selectedMonthEnd[0];
      } else {
        updates.month_end = "";
      }
    }

    console.log("🔄 Graph params updates:", updates);
    setGraphParams((prev) => ({ ...prev, ...updates }));
  };

  // Function to fetch graph data via react-query
  const cleanedGraphParams = useMemo(
    () => cleanParams(graphParams),
    [graphParams]
  );

  const graphQueryEnabled =
    isInitialized &&
    Boolean(graphParams.provider_npi) &&
    Boolean(
      graphParams.period_type ||
        graphParams.month_start ||
        graphParams.month_end ||
        graphParams.week_start ||
        graphParams.week_end
    ) &&
    !dateFiltersLoading;

  const { data: graphQueryResponse, isFetching: graphFetching } = useGraphData(
    cleanedGraphParams,
    graphQueryEnabled
  );

  useEffect(() => {
    if (Array.isArray(graphQueryResponse?.data?.data?.graphs)) {
      setApiData(graphQueryResponse.data.data);
    }
  }, [graphQueryResponse]);

  useEffect(() => {
    setLoading(graphFetching);
  }, [graphFetching]);
  // Add these functions to your second file, preferably after the convertToSelectOptions function

  // Helper function to extract month number from date range value
  const getMonthNumber = (dateRangeValue: string): number => {
    if (!dateRangeValue) return 0;
    const startDate = dateRangeValue.split(" - ")[0];
    const month = parseInt(startDate.split("-")[0], 10);
    return month;
  };

  // Validation function to check if start month is before end month
  const isValidDateRange = (
    startValue: string[],
    endValue: string[]
  ): boolean => {
    if (!startValue.length || !endValue.length) return true; // Allow empty selections
    const startMonth = getMonthNumber(startValue[0]);
    const endMonth = getMonthNumber(endValue[0]);
    return startMonth <= endMonth;
  };

  // Updated onChange handler for month start selection
  const handleMonthStartChange = (value: string[]) => {
    console.log("📅 Date Start selection changed:", value);

    const currentPeriodType = selectedPeriodType[0];

    if (currentPeriodType === "W") {
      // Use week validation
      const correctedValues = validateAndCorrectWeekSelection(
        true, // isStartChange
        value.length > 1 ? [value[value.length - 1]] : value,
        selectedMonthStart,
        selectedMonthEnd
      );

      setSelectedMonthStart(correctedValues.newStart);
      if (correctedValues.newEnd !== selectedMonthEnd) {
        setSelectedMonthEnd(correctedValues.newEnd);
      }
    } else {
      // Use month validation
      const newValue = value.length > 1 ? [value[value.length - 1]] : value;

      if (
        newValue.length === 1 &&
        selectedMonthEnd.length === 1 &&
        !isValidDateRange(newValue, selectedMonthEnd)
      ) {
        // If selected start date is after end date, adjust end date
        const startMonth = getMonthNumber(newValue[0]);
        const validEndOption = monthEndOptions.find(
          (option) => getMonthNumber(option.value) >= startMonth
        );
        if (validEndOption) {
          setSelectedMonthEnd([validEndOption.value]);
        }
      }
      setSelectedMonthStart(newValue);
    }
  };

  // Updated onChange handler for month end selection
  const handleMonthEndChange = (value: string[]) => {
    console.log("📅 Date End selection changed:", value);

    const currentPeriodType = selectedPeriodType[0];

    if (currentPeriodType === "W") {
      // Use week validation
      const correctedValues = validateAndCorrectWeekSelection(
        false, // isStartChange
        value.length > 1 ? [value[value.length - 1]] : value,
        selectedMonthStart,
        selectedMonthEnd
      );

      setSelectedMonthEnd(correctedValues.newEnd);
      if (correctedValues.newStart !== selectedMonthStart) {
        setSelectedMonthStart(correctedValues.newStart);
      }
    } else {
      // Use month validation
      const newValue = value.length > 1 ? [value[value.length - 1]] : value;

      if (
        newValue.length === 1 &&
        selectedMonthStart.length === 1 &&
        !isValidDateRange(selectedMonthStart, newValue)
      ) {
        // If selected end date is before start date, adjust start date
        const endMonth = getMonthNumber(newValue[0]);
        const validStartOption = monthStartOptions.find(
          (option) => getMonthNumber(option.value) <= endMonth
        );
        if (validStartOption) {
          setSelectedMonthStart([validStartOption.value]);
        }
      }
      setSelectedMonthEnd(newValue);
    }
  };
  // Function to validate and auto-correct week selections
  const validateAndCorrectWeekSelection = (
    isStartChange: boolean,
    newValue: string[],
    currentStartValue: string[],
    currentEndValue: string[]
  ): { newStart: string[]; newEnd: string[] } => {
    const currentPeriodType = selectedPeriodType[0];

    if (currentPeriodType !== "W") {
      // For monthly, use existing logic
      return { newStart: currentStartValue, newEnd: currentEndValue };
    }

    // For weekly validation
    const startOptions = convertToSelectOptions(startFilterData);
    const endOptions = convertToSelectOptions(endFilterData);

    if (isStartChange) {
      // User changed start week
      if (newValue.length === 1 && currentEndValue.length === 1) {
        const newStartOption = startOptions.find(
          (opt) => opt.value === newValue[0]
        );
        const currentEndOption = endOptions.find(
          (opt) => opt.value === currentEndValue[0]
        );

        if (newStartOption && currentEndOption) {
          const newStartWeek = getWeekNumber(newStartOption.label);
          const currentEndWeek = getWeekNumber(currentEndOption.label);

          if (newStartWeek > currentEndWeek) {
            // Start week is after end week, update end week to match or be after start week
            const validEndOption = endOptions.find(
              (opt) => getWeekNumber(opt.label) >= newStartWeek
            );
            if (validEndOption) {
              console.log(
                `📅 Auto-correcting end week from ${currentEndOption.label} to ${validEndOption.label}`
              );
              return { newStart: newValue, newEnd: [validEndOption.value] };
            }
          }
        }
      }
      return { newStart: newValue, newEnd: currentEndValue };
    } else {
      // User changed end week
      if (newValue.length === 1 && currentStartValue.length === 1) {
        const newEndOption = endOptions.find(
          (opt) => opt.value === newValue[0]
        );
        const currentStartOption = startOptions.find(
          (opt) => opt.value === currentStartValue[0]
        );

        if (newEndOption && currentStartOption) {
          const newEndWeek = getWeekNumber(newEndOption.label);
          const currentStartWeek = getWeekNumber(currentStartOption.label);

          if (newEndWeek < currentStartWeek) {
            // End week is before start week, update start week to match or be before end week
            const validStartOption = startOptions.find(
              (opt) => getWeekNumber(opt.label) <= newEndWeek
            );
            if (validStartOption) {
              console.log(
                `📅 Auto-correcting start week from ${currentStartOption.label} to ${validStartOption.label}`
              );
              return { newStart: [validStartOption.value], newEnd: newValue };
            }
          }
        }
      }
      return { newStart: currentStartValue, newEnd: newValue };
    }
  };

  // Initial load effect - STEP 1: Load all filter APIs
  useEffect(() => {
    console.log("🔄 Component mounted, starting initialization sequence...");
    loadFilterAPIs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // STEP 2: Set initial values when all filter data is loaded (or when some fail)
  useEffect(() => {
    // Initialize when filters loading is complete (regardless of success/failure)
    if (!filtersLoading && !isInitialized) {
      setInitialValues(
        facilitiesData,
        specialtiesData,
        startFilterData,
        endFilterData
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filtersLoading,
    isInitialized,
    facilitiesData,
    specialtiesData,
    startFilterData,
    endFilterData,
  ]);

  // NEW: Effect to reload date filters when period type changes
  useEffect(() => {
    // Only reload if component is initialized and period type has a value
    if (isInitialized && selectedPeriodType.length === 1) {
      const newPeriodType = selectedPeriodType[0];

      // Check if period type actually changed from the current filter params
      if (
        newPeriodType !== startFilterParams.period_type ||
        newPeriodType !== endFilterParams.period_type
      ) {
        console.log("🔄 Period type changed, reloading date filters...");
        reloadDateFilters(newPeriodType);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInitialized, selectedPeriodType]);

  // NEW: Effect to set date selections after date filters are reloaded
  useEffect(() => {
    // Only set selections if date filters are not loading and we have data
    if (
      !dateFiltersLoading &&
      isInitialized &&
      (startFilterData || endFilterData) &&
      selectedMonthStart.length === 0 &&
      selectedMonthEnd.length === 0
    ) {
      console.log("📅 Setting date selections after date filter reload...");
      setDateSelectionsAfterReload(startFilterData, endFilterData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dateFiltersLoading,
    startFilterData,
    endFilterData,
    isInitialized,
    selectedMonthStart.length,
    selectedMonthEnd.length,
  ]);

  // STEP 3: Update graph params when initial values are set or selections change
  useEffect(() => {
    if (
      isInitialized &&
      selectedPeriodType.length > 0 &&
      selectedMonthStart.length > 0 &&
      selectedMonthEnd.length > 0 &&
      !dateFiltersLoading
    ) {
      // Don't update while date filters are loading
      updateGraphParamsAndFetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isInitialized,
    selectedFacilities,
    selectedSpecialities,
    selectedPeriodType,
    selectedMonthStart,
    selectedMonthEnd,
    dateFiltersLoading,
  ]);

  // STEP 4: graph params drive useGraphData (see graphQueryEnabled above)

  // Add effect to log apiData changes
  useEffect(() => {
    if (apiData) {
      console.log("🔥 API Data updated:", apiData);
      console.log(
        "🔥 Available graphs:",
        apiData.graphs?.map((g) => ({
          title: g.chart_title,
          type: g.chart_type,
        }))
      );
      console.log("🔥 Total graphs found:", apiData.graphs?.length || 0);
    } else {
      console.log("🔥 API Data is null/undefined");
    }
  }, [apiData]);

  const getChartTypeFromApi = (chartTitle: string): ChartType => {
    console.log(`🔍 Getting chart type for: "${chartTitle}"`);

    if (!apiData || !apiData.graphs) {
      console.log(
        `⚠️ No API data available, returning default "bar" for: ${chartTitle}`
      );
      return "bar";
    }

    const chart = apiData.graphs.find(
      (graph: ApiChart) => graph.chart_title === chartTitle
    );
    if (!chart) {
      console.log(
        `⚠️ Chart not found, returning default "bar" for: ${chartTitle}`
      );
      return "bar";
    }

    const chartType = getChartType(chart.chart_type);
    console.log(
      `✅ Chart type for "${chartTitle}": ${chart.chart_type} -> ${chartType}`
    );
    return chartType;
  };

  // Create fallback data for the first chart that had fallback logic
  const fallbackData = createEmptyChartData();

  return (
    <div
      style={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflowY: "auto",
      }}
    >
      <Box
        style={{
          paddingTop: "8px",
          flexShrink: 0,
          width: "100%",
        }}
      >
        <Box
          className='dashboard-filters-scroll'
          style={{
            display: "flex",
            flexDirection: "row",

            gap: (() => {
              const width =
                typeof window !== "undefined" ? window.innerWidth : 1200;
              if (width < 480) return "8px";
              if (width < 680) return "8px";
              if (width < 900) return "6px";
              return "8px";
            })(),
            flexWrap: "nowrap",
            overflowX: "auto",
            paddingBottom: "8px",
          }}
        >
          {/* Facilities Filter */}
          <Box
            style={{
              minWidth: (() => {
                const width =
                  typeof window !== "undefined" ? window.innerWidth : 1200;
                if (width < 480) return "110px";
                if (width < 600) return "110px";
                if (width < 768) return "110px";
                if (width < 900) return "150px";
                return "150px";
              })(),
              flex: "1 1 auto",
              maxWidth: (() => {
                const width =
                  typeof window !== "undefined" ? window.innerWidth : 1200;
                if (width < 600) return "110px";
                if (width < 768) return "110px";
                if (width < 900) return "150px";
                return "150px";
              })(),
            }}
          >
            <CustomSelect
              options={facilitiesOptions}
              value={selectedFacilities}
              onChange={(value) => {
                console.log("🏥 Facilities selection changed:", value);
                if (value.length > 1) {
                  setSelectedFacilities([value[value.length - 1]]);
                } else {
                  setSelectedFacilities(value);
                  if (value.length === 0) {
                    console.log("🏥 Facilities cleared, resetting search");
                    setFacilitiesSearchTerm("");
                    if (facilitiesSearchTimeoutRef.current) {
                      clearTimeout(facilitiesSearchTimeoutRef.current);
                    }
                    handleFacilitiesSearch("");
                  }
                }
              }}
              placeholder='All Facilities'
              label='Facilities'
              searchable={true}
              searchablethroughapi={true}
              onSearch={handleFacilitiesSearch}
              searchLoading={facilitiesSearchLoading}
              error={facilitiesError}
              errorMessage='Failed to load facilities'
              maxHeight={200}
              disabled={filtersLoading}
              size={(() => {
                const width =
                  typeof window !== "undefined" ? window.innerWidth : 1200;
                if (width < 480) return "xs";
                if (width < 768) return "md";
                return "md";
              })()}
              allowShrink={true}
            />
          </Box>

          {/* Specialties Filter */}
          <Box
            style={{
              minWidth: (() => {
                const width =
                  typeof window !== "undefined" ? window.innerWidth : 1200;
                if (width < 480) return "110px";
                if (width < 600) return "110px";
                if (width < 768) return "150px";
                if (width < 900) return "150px";
                return "150px";
              })(),
              flex: "1 1 auto",
              maxWidth: (() => {
                const width =
                  typeof window !== "undefined" ? window.innerWidth : 1200;
                if (width < 600) return "110px";
                if (width < 768) return "150px";
                if (width < 900) return "150px";
                return "150px";
              })(),
            }}
          >
            <CustomSelect
              options={specialitiesOptions}
              value={selectedSpecialities}
              onChange={(value) => {
                console.log("🔬 Specialties selection changed:", value);
                if (value.length > 1) {
                  setSelectedSpecialities([value[value.length - 1]]);
                } else {
                  setSelectedSpecialities(value);
                  if (value.length === 0) {
                    console.log("🔬 Specialties cleared, resetting search");
                    setSpecialtiesSearchTerm("");
                    if (specialtiesSearchTimeoutRef.current) {
                      clearTimeout(specialtiesSearchTimeoutRef.current);
                    }
                    handleSpecialtiesSearch("");
                  }
                }
              }}
              placeholder='All Specialties'
              label='Specialties'
              searchable={true}
              searchablethroughapi={true}
              onSearch={handleSpecialtiesSearch}
              searchLoading={specialtiesSearchLoading}
              error={specialtiesError}
              errorMessage='Failed to load specialties'
              maxHeight={200}
              disabled={filtersLoading}
              size={(() => {
                const width =
                  typeof window !== "undefined" ? window.innerWidth : 1200;
                if (width < 480) return "xs";
                if (width < 768) return "md";
                return "md";
              })()}
              allowShrink={true}
            />
          </Box>

          {/* Period Type Filter */}
          <Box
            style={{
              minWidth: (() => {
                const width =
                  typeof window !== "undefined" ? window.innerWidth : 1200;
                if (width < 480) return "85px";
                if (width < 600) return "85px";
                if (width < 768) return "85px";
                if (width < 900) return "85px";
                return "150px";
              })(),
              flex: "0.8 1 auto",
              maxWidth: (() => {
                const width =
                  typeof window !== "undefined" ? window.innerWidth : 1200;
                if (width < 600) return "110px";
                if (width < 768) return "150px";
                if (width < 900) return "150px";
                return "150px";
              })(),
            }}
          >
            <CustomSelect
              options={periodTypeSelectOptions}
              value={selectedPeriodType}
              onChange={(value) => {
                console.log("📅 Period type selection changed:", value);
                if (value.length > 1) {
                  setSelectedPeriodType([value[value.length - 1]]);
                } else {
                  setSelectedPeriodType(value);
                }
              }}
              placeholder='Select Period'
              label='Metrics'
              searchable={false}
              error={periodTypeError}
              errorMessage='Failed to load period types'
              maxHeight={200}
              disabled={filtersLoading}
              size={(() => {
                const width =
                  typeof window !== "undefined" ? window.innerWidth : 1200;
                if (width < 480) return "xs";
                if (width < 768) return "md";
                return "md";
              })()}
              allowShrink={true}
            />
          </Box>

          {/* Start Date Filter */}
          <Box
            style={{
              minWidth: (() => {
                const width =
                  typeof window !== "undefined" ? window.innerWidth : 1200;
                if (width < 480) return "110px";
                if (width < 600) return "110px";
                if (width < 768) return "150px";
                if (width < 900) return "150px";
                return "150px";
              })(),
              flex: "1 1 auto",
              maxWidth: (() => {
                const width =
                  typeof window !== "undefined" ? window.innerWidth : 1200;
                if (width < 600) return "120px";
                if (width < 768) return "150px";
                if (width < 900) return "150px";
                return "150px";
              })(),
            }}
          >
            <CustomSelect
              options={monthStartOptions}
              value={selectedMonthStart}
              onChange={handleMonthStartChange}
              placeholder={
                selectedPeriodType[0] === "W" ? "Start Week" : "Start Month"
              }
              label={
                selectedPeriodType[0] === "W"
                  ? "Week starting on"
                  : "Month starting on"
              }
              searchable={true}
              error={startFilterError}
              errorMessage={
                selectedPeriodType[0] === "W"
                  ? "Failed to load start weeks"
                  : "Failed to load start months"
              }
              maxHeight={200}
              disabled={filtersLoading || dateFiltersLoading}
              size={(() => {
                const width =
                  typeof window !== "undefined" ? window.innerWidth : 1200;
                if (width < 480) return "xs";
                if (width < 768) return "md";
                return "md";
              })()}
              allowShrink={true}
            />
          </Box>

          {/* End Date Filter */}
          <Box
            style={{
              minWidth: (() => {
                const width =
                  typeof window !== "undefined" ? window.innerWidth : 1200;
                if (width < 480) return "110px";
                if (width < 600) return "110px";
                if (width < 768) return "150px";
                if (width < 900) return "150px";
                return "150px";
              })(),
              flex: "1 1 auto",
              maxWidth: (() => {
                const width =
                  typeof window !== "undefined" ? window.innerWidth : 1200;
                if (width < 600) return "120px";
                if (width < 768) return "150px";
                if (width < 900) return "150px";
                return "150px";
              })(),
            }}
          >
            <CustomSelect
              options={monthEndOptions}
              value={selectedMonthEnd}
              onChange={handleMonthEndChange}
              placeholder={
                selectedPeriodType[0] === "W" ? "End Week" : "End Month"
              }
              label={
                selectedPeriodType[0] === "W"
                  ? "Week ending on"
                  : "Month ending on"
              }
              searchable={true}
              error={endFilterError}
              errorMessage={
                selectedPeriodType[0] === "W"
                  ? "Failed to load end weeks"
                  : "Failed to load end months"
              }
              maxHeight={200}
              disabled={filtersLoading || dateFiltersLoading}
              size={(() => {
                const width =
                  typeof window !== "undefined" ? window.innerWidth : 1200;
                if (width < 480) return "xs";
                if (width < 768) return "md";
                return "md";
              })()}
              allowShrink={true}
            />
          </Box>
        </Box>
      </Box>

      <Box
        style={{
          flex: 1,
          paddingRight: "8px",
          position: "relative",
        }}
      >
        <Stack gap={isMobile ? "md" : "xl"} style={{ paddingTop: "5px" }}>
          <Grid gutter={isMobile ? "xs" : "md"}>
            <Grid.Col span={{ base: isMobile ? 12 : 6, md: 4 }}>
              <Box>
                <DataChart
                  type={getChartTypeFromApi("Total Queries")}
                  title={getChartTitle("Total Queries")}
                  data={getChartData("Total Queries") || fallbackData}
                  height={height}
                  width={width}
                  {...getChartAxisTitles("Total Queries")}
                  loading={loading || filtersLoading || dateFiltersLoading}
                />
              </Box>
            </Grid.Col>
            <Grid.Col span={{ base: isMobile ? 12 : 6, md: 4 }}>
              <Box>
                <DataChart
                  type={getChartTypeFromApi("Pending Queries")}
                  title={getChartTitle("Pending Queries")}
                  data={getChartData("Pending Queries")}
                  height={height}
                  width={width}
                  {...getChartAxisTitles("Pending Queries")}
                  loading={loading || filtersLoading || dateFiltersLoading}
                />
              </Box>
            </Grid.Col>
            <Grid.Col span={{ base: isMobile ? 12 : 6, md: 4 }}>
              <Box>
                <DataChart
                  type={getChartTypeFromApi("Query Agree Rate")}
                  title={getChartTitle("Query Agree Rate")}
                  data={getChartData("Query Agree Rate")}
                  height={height}
                  width={width}
                  {...getChartAxisTitles("Query Agree Rate")}
                  loading={loading || filtersLoading || dateFiltersLoading}
                />
              </Box>
            </Grid.Col>
            <Grid.Col span={{ base: isMobile ? 12 : 6, md: 4 }}>
              <Box>
                <DataChart
                  type={getChartTypeFromApi("Query Response Time")}
                  title={getChartTitle("Query Response Time")}
                  data={getChartData("Query Response Time")}
                  height={height}
                  width={width}
                  {...getChartAxisTitles("Query Response Time")}
                  loading={loading || filtersLoading || dateFiltersLoading}
                />
              </Box>
            </Grid.Col>

            <Grid.Col span={{ base: isMobile ? 12 : 6, md: 4 }}>
              <Box>
                <DataChart
                  type={getChartTypeFromApi("Query Response Rate")}
                  title={getChartTitle("Query Response Rate")}
                  data={getChartData("Query Response Rate")}
                  height={height}
                  width={width}
                  {...getChartAxisTitles("Query Response Rate")}
                  loading={loading || filtersLoading || dateFiltersLoading}
                />
              </Box>
            </Grid.Col>

            <Grid.Col span={{ base: isMobile ? 12 : 6, md: 4 }}>
              <Box>
                <DataChart
                  type={getChartTypeFromApi("Top 10 Query Topics") ?? "line"}
                  title={getChartTitle("Top 10 Query Topics")}
                  data={getChartData("Top 10 Query Topics")}
                  height={height}
                  width={width}
                  {...getChartAxisTitles("Top 10 Query Topics")}
                  loading={loading || filtersLoading || dateFiltersLoading}
                />
              </Box>
            </Grid.Col>
          </Grid>
        </Stack>
      </Box>
    </div>
  );
};

export default TestCharts;
